import { uuid4 } from '@sentry/utils';
import { AxiosError, AxiosRequestConfig } from 'axios';
import { DropzoneFile } from 'components/Dropzone/utils';
import {
  MAX_BATCH_SIZE,
  MAX_BATCH_SIZE_GB,
  SINGLE_FILE_MAX_SIZE,
  SINGLE_FILE_MAX_SIZE_GB,
} from 'data/global';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { ACCEPTED_FILE_TYPES } from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/data/DataTypes';
import useErrors from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/hooks/useErrors';
import { BATCH_SIZE, createDropzoneFile } from 'uploadFileUtils';
import {
  formatMemoryLabel,
  getPercentageWithTwoDecimals,
  getTotalFilesSize,
} from 'utils';

const MAX_FILES = 10;

export const getIsFileUploaded = ({
  percentage,
  isFailed,
}: UploaderMessageFile) => percentage === 100 && !isFailed;

export const getIsFileProcessed = ({
  percentage,
  isFailed,
}: UploaderMessageFile) => percentage === 100 || isFailed;

export const createUploadingFiles = (files: DropzoneFile[]) =>
  files.map((el) => ({ ...el, isFailed: false, percentage: 100 }));

export type UploaderMessageFile = Partial<DropzoneFile> & {
  isFailed: boolean;
  percentage: number;
};

type UseUploadMessageFileProps = {
  initialValues?: DropzoneFile[];
  onUploadReq: (
    file: File,
    config: AxiosRequestConfig,
  ) => Promise<DropzoneFile>;
  onAddFile?: (file: DropzoneFile) => void;
  onRemoveFile?: (fileId: string, file: DropzoneFile) => void;
};

const useUploadMessageFile = ({
  initialValues = [],
  onUploadReq,
  onAddFile,
  onRemoveFile,
}: UseUploadMessageFileProps) => {
  const [files, setFiles] = useState<UploaderMessageFile[]>(
    createUploadingFiles(initialValues),
  );

  const { popError } = useContext(PopNotificationsContext);

  const { popCustomUploadError } = useErrors();

  const { t } = useTranslation();

  const uploadedFiles = useMemo(
    () =>
      files
        .filter(getIsFileUploaded)
        .map(
          ({ isFailed, percentage, _id, ...rest }) => rest,
        ) as DropzoneFile[],
    [files],
  );

  const areAllFilesProcessed = useMemo(
    () => files.every(getIsFileProcessed),
    [files],
  );

  const createFileConfig = useCallback(
    (_id: string, file: File) =>
      setFiles((old) => [
        ...old,
        {
          ...createDropzoneFile(file),
          _id,
          percentage: 0,
          isFailed: false,
        },
      ]),
    [],
  );

  const updateFileConfig = useCallback(
    (fileId: string) => ({
      onUploadProgress: (progressEvent: ProgressEvent) => {
        const { total, loaded } = progressEvent;
        const uploadPercentage = getPercentageWithTwoDecimals(loaded, total);
        const percentage =
          uploadPercentage === 100 ? uploadPercentage - 1 : uploadPercentage;

        setFiles((old) =>
          old.map((el) =>
            el._id === fileId
              ? {
                  ...el,
                  percentage,
                  size: total,
                }
              : el,
          ),
        );
      },
    }),
    [],
  );

  const updateUploadedFile = useCallback(
    (fileId: string, { name, ...rest }: DropzoneFile) =>
      setFiles((old) =>
        old.map((el) =>
          el._id === fileId
            ? {
                ...el,
                ...rest,
                percentage: 100,
              }
            : el,
        ),
      ),
    [],
  );

  const updateFailedFileConfig = useCallback(
    (fileId: string) =>
      setFiles((old) =>
        old.map((el) =>
          el._id === fileId
            ? {
                ...el,
                percentage: 0,
                isFailed: true,
              }
            : el,
        ),
      ),
    [],
  );

  const uploadRequest = useCallback(
    async (acceptedFiles: File[]) => {
      let nextToProcess = 0;

      await Promise.all(
        Array.from({ length: BATCH_SIZE }).map(async (_) => {
          while (nextToProcess < acceptedFiles.length) {
            const currentlyProcessing = nextToProcess++;

            const uploadId = uuid4();

            const file = acceptedFiles[currentlyProcessing];

            createFileConfig(uploadId, file);

            try {
              const uploadedFile = await onUploadReq(
                file,
                updateFileConfig(uploadId),
              );

              updateUploadedFile(uploadId, uploadedFile);
              onAddFile?.({
                ...uploadedFile,
                ...createDropzoneFile(file),
              });
            } catch (e) {
              updateFailedFileConfig(uploadId);
              popCustomUploadError(e as AxiosError);
            }
          }
        }),
      );
    },
    [
      createFileConfig,
      onUploadReq,
      onAddFile,
      updateFileConfig,
      updateUploadedFile,
      updateFailedFileConfig,
      popCustomUploadError,
    ],
  );

  const onFileRemove = useCallback(
    (fileId: string) => {
      setFiles((old) => {
        const fileToRemove = old.find(({ _id }) => _id === fileId);
        if (fileToRemove) {
          const { isFailed, percentage, _id, id, ...rest } = fileToRemove;
          onRemoveFile?.(_id, rest as DropzoneFile);
        }

        return old.filter(({ _id }) => _id !== fileId);
      });
    },
    [onRemoveFile],
  );

  const clearFiles = useCallback(() => setFiles([]), []);

  const onDropCallback = useCallback(
    (acceptedFiles: any[]) => {
      const totalSize = getTotalFilesSize(acceptedFiles);

      if (totalSize > MAX_BATCH_SIZE) {
        popError(
          t('uploadBatchTooLarge', {
            fileLimit: formatMemoryLabel(SINGLE_FILE_MAX_SIZE_GB),
            batchLimit: formatMemoryLabel(MAX_BATCH_SIZE_GB),
          }),
        );
        return;
      }

      uploadRequest(acceptedFiles);
    },
    [popError, uploadRequest, t],
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: ACCEPTED_FILE_TYPES,
    maxSize: SINGLE_FILE_MAX_SIZE,
    maxFiles: MAX_FILES,
    useFsAccessApi: false,
    onDrop: onDropCallback,
  });

  const { onClick: onFileUpload, ...restRootProps } = getRootProps();

  return {
    files,
    uploadedFiles,
    areAllFilesProcessed,
    restRootProps,
    onFileRemove,
    onFileUpload,
    clearFiles,
    getInputProps,
  };
};

export default useUploadMessageFile;
